/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Present the CL-F05 preview, success, loading, and safe refusal states.
 * @state-owner component-local preview and acceptance state
 * @allowed-imports React; workspace environment/session; classroom-join transport; shared loading; auth public screen
 * @forbidden shared router registration; CL-F03 renderer; client authorization decisions
 * @public-api ClassroomJoinPage and ClassroomJoinEntry
 * @side-effects token preview and acceptance commands
 * @tests ClassroomJoinPage.test.tsx and CL-F05 Playwright flow
 */

import { VispoApiError } from '@vispo/api-client';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { useWorkspaceSession } from '../../../app/providers/SessionProvider';
import { VispoBrandLogo } from '../../../shared/components/BrandMarks';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { useIsMobile } from '../../../shared/model/use-workspace';
import { SignInScreen } from '../../auth';
import {
  acceptClassroomGrantRequest,
  type ClassroomGrantAcceptance,
  type ClassroomGrantPreview,
  declineClassroomInvitationRequest,
  previewClassroomGrantRequest,
} from '../api/classroom-join';
import './classroom-join.css';

export type ClassroomJoinSurface = 'desktop' | 'mobile';

interface JoinState {
  phase: 'pending' | 'preview' | 'success' | 'declined' | 'refusal';
  preview?: ClassroomGrantPreview;
  acceptance?: ClassroomGrantAcceptance;
  message?: string;
}

function refusalMessage(error: unknown): string {
  if (error instanceof VispoApiError) {
    if (error.code === 'CLASSROOM_GRANT_EXHAUSTED') return '這個加入連結已達使用上限。';
  }
  return '這個連結無法使用，請確認連結完整，或請對方重新發送。';
}

function workspaceDestination(grantRole: ClassroomGrantPreview['grantRole']): string {
  return grantRole === 'teacher' ? '/teacher/classes' : '/student/classes';
}

function joinLoginPath(): string {
  return '/student/login';
}

function DesktopJoinSurface({
  state,
  busy,
  onAccept,
  onDecline,
}: {
  state: JoinState;
  busy: boolean;
  onAccept(): void;
  onDecline(): void;
}) {
  if (state.phase === 'pending') {
    return <VispoLoading className="workspace-loading" label="正在確認邀請內容…" />;
  }
  if (state.phase === 'success') {
    return (
      <main className="classroom-join classroom-join--desktop" data-surface="desktop">
        <section className="classroom-join__card classroom-join__card--success" aria-live="polite">
          <span className="classroom-join__check" aria-hidden="true">
            ✓
          </span>
          <h1>已加入班級</h1>
          <p>你現在可以在 Vispo 工作區查看這個班級。</p>
          <a className="bt pri" href={workspaceDestination(state.preview?.grantRole ?? 'student')}>
            前往我的班級
          </a>
        </section>
      </main>
    );
  }
  if (state.phase === 'refusal') {
    return (
      <main className="classroom-join classroom-join--desktop" data-surface="desktop">
        <section className="classroom-join__card" role="alert">
          <h1>連結無法使用</h1>
          <p>{state.message}</p>
          <a className="bt" href={joinLoginPath()}>
            回到登入頁
          </a>
        </section>
      </main>
    );
  }
  if (state.phase === 'declined') {
    return (
      <main className="classroom-join classroom-join--desktop" data-surface="desktop">
        <section className="classroom-join__card" aria-live="polite">
          <h1>已拒絕邀請</h1>
          <p>這個邀請不會加入你的班級。</p>
          <a className="bt" href={workspaceDestination(state.preview?.grantRole ?? 'student')}>
            回到我的班級
          </a>
        </section>
      </main>
    );
  }
  const preview = state.preview;
  return (
    <main className="classroom-join classroom-join--desktop" data-surface="desktop">
      <section className="classroom-join__card" aria-labelledby="classroom-join-title">
        <span className="classroom-join__eyebrow">CLASSROOM INVITATION</span>
        <h1 id="classroom-join-title">確認加入班級</h1>
        <div className="classroom-join__preview">
          <span>班級</span>
          <strong>{preview?.classroomName}</strong>
          <span>加入身分</span>
          <strong>{preview?.grantRole === 'teacher' ? '老師' : '學生'}</strong>
        </div>
        <p>請確認班級與身分，接受後會取得對應的班級權限。</p>
        <div className="classroom-join__actions">
          <button
            className="bt pri classroom-join__primary"
            disabled={busy}
            type="button"
            onClick={onAccept}
          >
            {busy ? '正在加入…' : '接受加入'}
          </button>
          {preview?.kind === 'invitation' ? (
            <button className="bt" disabled={busy} type="button" onClick={onDecline}>
              拒絕邀請
            </button>
          ) : null}
        </div>
      </section>
    </main>
  );
}

function MobileJoinSurface({
  state,
  busy,
  onAccept,
  onDecline,
}: {
  state: JoinState;
  busy: boolean;
  onAccept(): void;
  onDecline(): void;
}) {
  if (state.phase === 'pending') {
    return <VispoLoading className="workspace-loading" label="正在確認邀請內容…" />;
  }
  if (state.phase === 'success') {
    return (
      <main className="classroom-join classroom-join--mobile" data-surface="mobile">
        <section
          className="classroom-join__mobile-card classroom-join__mobile-card--success"
          aria-live="polite"
        >
          <span className="classroom-join__check" aria-hidden="true">
            ✓
          </span>
          <h1>已加入班級</h1>
          <p>班級已加入你的工作區。</p>
          <a className="bt pri" href={workspaceDestination(state.preview?.grantRole ?? 'student')}>
            前往我的班級
          </a>
        </section>
      </main>
    );
  }
  if (state.phase === 'refusal') {
    return (
      <main className="classroom-join classroom-join--mobile" data-surface="mobile">
        <section className="classroom-join__mobile-card" role="alert">
          <h1>連結無法使用</h1>
          <p>{state.message}</p>
          <a className="bt" href={joinLoginPath()}>
            回到登入頁
          </a>
        </section>
      </main>
    );
  }
  if (state.phase === 'declined') {
    return (
      <main className="classroom-join classroom-join--mobile" data-surface="mobile">
        <section className="classroom-join__mobile-card" aria-live="polite">
          <h1>已拒絕邀請</h1>
          <p>這個邀請不會加入你的班級。</p>
          <a className="bt" href={workspaceDestination(state.preview?.grantRole ?? 'student')}>
            回到我的班級
          </a>
        </section>
      </main>
    );
  }
  const preview = state.preview;
  return (
    <main className="classroom-join classroom-join--mobile" data-surface="mobile">
      <header className="classroom-join__mobile-header">
        <VispoBrandLogo size={32} />
      </header>
      <section
        className="classroom-join__mobile-card"
        aria-labelledby="classroom-join-mobile-title"
      >
        <span className="classroom-join__eyebrow">邀請加入</span>
        <h1 id="classroom-join-mobile-title">確認加入班級</h1>
        <div className="classroom-join__mobile-classroom">
          <span>班級</span>
          <strong>{preview?.classroomName}</strong>
          <small>{preview?.grantRole === 'teacher' ? '老師身分' : '學生身分'}</small>
        </div>
        <p>接受後，你會看到這個班級與對應的學習內容。</p>
        <div className="classroom-join__actions">
          <button
            className="bt pri classroom-join__primary"
            disabled={busy}
            type="button"
            onClick={onAccept}
          >
            {busy ? '正在加入…' : '接受加入'}
          </button>
          {preview?.kind === 'invitation' ? (
            <button className="bt" disabled={busy} type="button" onClick={onDecline}>
              拒絕邀請
            </button>
          ) : null}
        </div>
      </section>
    </main>
  );
}

export function ClassroomJoinPage({
  token,
  surface,
}: {
  token: string;
  surface: ClassroomJoinSurface;
}) {
  const { apiClient } = useWorkspaceEnvironment();
  const { isLoading, session } = useWorkspaceSession();
  const [state, setState] = useState<JoinState>({ phase: 'pending' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isLoading || !session) return;
    let active = true;
    void previewClassroomGrantRequest(apiClient, token)
      .then((preview) => {
        if (active) setState({ phase: 'preview', preview });
      })
      .catch((error: unknown) => {
        if (active) setState({ phase: 'refusal', message: refusalMessage(error) });
      });
    return () => {
      active = false;
    };
  }, [apiClient, isLoading, session, token]);

  if (isLoading) return <VispoLoading className="workspace-loading" label="正在確認登入狀態…" />;
  if (!session) return <SignInScreen audience="student" returnTo={`/join/${token}`} />;

  const onAccept = () => {
    const preview = state.preview;
    if (!preview) return;
    setBusy(true);
    void acceptClassroomGrantRequest(apiClient, preview, token)
      .then((acceptance) => setState({ phase: 'success', preview, acceptance }))
      .catch((error: unknown) =>
        setState({ phase: 'refusal', preview, message: refusalMessage(error) }),
      )
      .finally(() => setBusy(false));
  };

  const onDecline = () => {
    const preview = state.preview;
    if (preview?.kind !== 'invitation') return;
    setBusy(true);
    void declineClassroomInvitationRequest(apiClient, preview.grantId, token)
      .then(() => setState({ phase: 'declined', preview }))
      .catch((error: unknown) =>
        setState({ phase: 'refusal', preview, message: refusalMessage(error) }),
      )
      .finally(() => setBusy(false));
  };

  return surface === 'desktop' ? (
    <DesktopJoinSurface state={state} busy={busy} onAccept={onAccept} onDecline={onDecline} />
  ) : (
    <MobileJoinSurface state={state} busy={busy} onAccept={onAccept} onDecline={onDecline} />
  );
}

export function ClassroomJoinEntry() {
  const { token = '' } = useParams<{ token: string }>();
  const { platform } = useWorkspaceEnvironment();
  const mobile = useIsMobile();
  return (
    <ClassroomJoinPage
      token={token}
      surface={platform === 'desktop' || !mobile ? 'desktop' : 'mobile'}
    />
  );
}
